import { useEffect, useRef } from 'react'

const FOCUSABLE_SELECTORS = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled])',
  'textarea:not([disabled])',
  'select:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
  '[contenteditable]',
] as const

function getFocusableElements(container: HTMLElement): HTMLElement[] {
  return Array.from(
    container.querySelectorAll(FOCUSABLE_SELECTORS.join(','))
  ).filter((el): el is HTMLElement => el instanceof HTMLElement)
}

/** Safely focus an element; silently handles elements that cannot be focused */
function safeFocus(el: HTMLElement | null | undefined): void {
  if (!el) return
  try {
    // Ensure element is still in DOM and visible before focusing
    if (!el.isConnected) return
    el.focus()
  } catch {
    // Ignore focus failures (hidden, disabled, or unmounted elements)
  }
}

interface UseFocusTrapOptions {
  onEscape?: () => void
  returnFocusOnDeactivate?: boolean
}

export function useFocusTrap(isActive: boolean, options?: UseFocusTrapOptions) {
  const containerRef = useRef<HTMLDivElement>(null)
  const previouslyFocusedRef = useRef<HTMLElement | null>(null)

  useEffect(() => {
    if (!isActive) return

    // Save previously focused element
    previouslyFocusedRef.current = document.activeElement as HTMLElement | null

    const container = containerRef.current
    if (!container) return

    // Ensure container itself is focusable so it can receive focus if no children
    if (!container.hasAttribute('tabindex')) {
      container.setAttribute('tabindex', '-1')
    }

    // Find focusable elements and focus the first one after a short delay
    // to allow the browser to paint the modal
    const rafId = requestAnimationFrame(() => {
      const focusable = getFocusableElements(container)
      if (focusable.length > 0) {
        safeFocus(focusable[0])
      } else {
        safeFocus(container)
      }
    })

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && options?.onEscape) {
        e.preventDefault()
        options.onEscape()
        return
      }

      if (e.key !== 'Tab' || !container) return

      const focusable = getFocusableElements(container)
      if (focusable.length === 0) {
        e.preventDefault()
        return
      }

      const first = focusable[0]
      const last = focusable[focusable.length - 1]

      if (e.shiftKey) {
        if (document.activeElement === first) {
          e.preventDefault()
          safeFocus(last)
        }
      } else {
        if (document.activeElement === last) {
          e.preventDefault()
          safeFocus(first)
        }
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => {
      cancelAnimationFrame(rafId)
      document.removeEventListener('keydown', handleKeyDown)
      if (options?.returnFocusOnDeactivate !== false && previouslyFocusedRef.current) {
        safeFocus(previouslyFocusedRef.current)
      }
    }
  }, [isActive, options?.onEscape, options?.returnFocusOnDeactivate])

  return containerRef
}
